import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export interface SelectedOption {
  optionId: string;
  value: string | string[] | number;
  label: string;
  priceImpact: number;
}

export interface OrderData {
  serviceId: string;
  serviceName: string;
  basePrice: number;
  currency: string;
  selectedOptions: SelectedOption[];
  totalPrice: number;
  source: 'service-page' | 'pricing-page';
  pricingTier?: 'start' | 'pro' | 'business';
}

interface OrderContextType {
  order: OrderData | null;
  setOrder: (order: OrderData) => void;
  clearOrder: () => void;
  updateOption: (optionId: string, value: string | string[] | number, label: string, priceImpact: number) => void;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export function OrderProvider({ children }: { children: ReactNode }) {
  const [order, setOrderState] = useState<OrderData | null>(null);

  const setOrder = useCallback((newOrder: OrderData) => {
    setOrderState(newOrder);
  }, []);

  const clearOrder = useCallback(() => {
    setOrderState(null);
  }, []);

  const updateOption = useCallback((
    optionId: string,
    value: string | string[] | number,
    label: string,
    priceImpact: number
  ) => {
    setOrderState(prev => {
      if (!prev) return prev;

      const existingIndex = prev.selectedOptions.findIndex(o => o.optionId === optionId);
      let newOptions: SelectedOption[];

      if (existingIndex >= 0) {
        newOptions = [...prev.selectedOptions];
        newOptions[existingIndex] = { optionId, value, label, priceImpact };
      } else {
        newOptions = [...prev.selectedOptions, { optionId, value, label, priceImpact }];
      }

      const newTotal = prev.basePrice + newOptions.reduce((sum, opt) => sum + opt.priceImpact, 0);

      return {
        ...prev,
        selectedOptions: newOptions,
        totalPrice: newTotal
      };
    });
  }, []);

  return (
    <OrderContext.Provider value={{ order, setOrder, clearOrder, updateOption }}>
      {children}
    </OrderContext.Provider>
  );
}

export function useOrder() {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within OrderProvider');
  }
  return context;
}
